// App.jsx - Converted back to JavaScript. Uses redirect flow for MSAL, handles Teams SSO, and blocks routes until authentication finishes.

import { useEffect, useState } from "react";
import {
  FluentProvider,
  teamsLightTheme,
  teamsDarkTheme,
  teamsHighContrastTheme,
  tokens,
} from "@fluentui/react-components";
import { useTeamsUserCredential } from "@microsoft/teamsfx-react";
import { TeamsFxContext } from "./Context";
import FeedbackAppTab from "./feedbackAppTab/FeedbackAppTab";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import FeedbackAppTabConfig from "./feedbackAppTab/feedbackAppTabConfig";
import { FeedbackAppTabRemove } from "./feedbackAppTab/FeedbackAppTabRemove";
import config from "./lib/config";
import { msalInstance } from "../services/http-service";
import * as microsoftTeams from "@microsoft/teams-js";

const SCOPES = ["User.Read", "OnlineMeetings.Read"];

function RouteLogger() {
  const location = useLocation();
  useEffect(() => {
    console.log("Current Route:", location.pathname);
  }, [location]);
  return null;
}

export default function App() {
  const { loading, theme, themeString, teamsUserCredential, inTeams } =
    useTeamsUserCredential({
      initiateLoginEndpoint: config.initiateLoginEndpoint,
      clientId: config.clientId,
    });

  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    console.log("inTeams", inTeams);
    console.log("inTeams Loading", loading);
    console.log("inTeams Theme", theme);
    console.log("inTeams ThemeString", themeString);
    console.log("inTeams teamsUserCredential", teamsUserCredential);
  }, [loading, theme, themeString, teamsUserCredential, inTeams]);

  useEffect(() => {
    // perform authentication only after teamsfx hook finished initial loading
    if (loading) return;

    let mounted = true;

    (async () => {
      const setAccessToken = (token: any) => {
        if (!mounted) return;
        if (token) sessionStorage.setItem("accessToken", token);
      };

      if (inTeams) {
        try {
          await microsoftTeams.app.initialize();
          try {
            const ssoToken = await microsoftTeams.authentication.getAuthToken();
            console.log("Teams SSO token acquired");
            setAccessToken(ssoToken);
          } catch (teamsError) {
            console.warn("Teams SSO failed:", teamsError);
          }
        } catch (initErr) {
          console.warn("Teams SDK initialization failed:", initErr);
        } finally {
          if (mounted) setAuthReady(true);
        }
        return;
      }

      try {
        console.log("Using MSAL flow (browser)...");
        await msalInstance.initialize();
        try {
          const redirectResult = await msalInstance.handleRedirectPromise();
          if (redirectResult) {
            console.log("MSAL redirect result:", redirectResult);
            if (redirectResult.accessToken)
              setAccessToken(redirectResult.accessToken);
          }
        } catch (handleErr) {
          console.warn("handleRedirectPromise error:", handleErr);
        }

        if ((msalInstance.getAllAccounts?.() ?? []).length === 0) {
          console.log("No MSAL accounts - redirecting to login");
          await msalInstance.loginRedirect({ scopes: SCOPES });
          return;
        }

        const accounts = msalInstance.getAllAccounts();
        const account = accounts[0];
        const request = { scopes: SCOPES, account };
        try {
          const tokenResponse = await msalInstance.acquireTokenSilent(request);
          console.log("Silent MSAL token acquired");
          setAccessToken(tokenResponse?.accessToken);
        } catch (silentErr) {
          console.warn(
            "Silent token failed; falling back to redirect",
            silentErr
          );
          await msalInstance.acquireTokenRedirect(request);
          return;
        }
      } catch (err) {
        console.error("Authentication error (MSAL):", err);
      } finally {
        if (mounted) setAuthReady(true);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [loading, inTeams]);

  if (loading || !authReady) {
    return (
      <div
        style={{
          display: "flex",
          position: "sticky",
          justifyContent: "center",
          verticalAlign: "middle",
          zIndex: "1000",
        }}
      >
        <img
          className="loader"
          style={{ width: "100px", position: "fixed", top: "40%", left: "40%" }}
          src="/assets/loader.gif"
          alt="Loading..."
        />
      </div>
    );
  }

  if (!inTeams) {
    return (
      <div>
        <p>Kindly access the Interview invitation to submit feedback</p>
      </div>
    );
  }

  return (
    <TeamsFxContext.Provider
      value={{ theme, themeString, teamsUserCredential }}
    >
      <FluentProvider
        theme={
          themeString === "dark"
            ? teamsDarkTheme
            : themeString === "contrast"
            ? teamsHighContrastTheme
            : {
                ...teamsLightTheme,
                colorNeutralBackground1: "#f5f5f5",
              }
        }
        style={{ background: tokens.colorNeutralBackground3, width: "100vw" }}
      >
        <Router>
          <RouteLogger />
          <Routes>
            <Route path="/" element={<FeedbackAppTab />} />
            <Route path="/Feedbackapptab" element={<FeedbackAppTab />} />
            <Route path="/feedbackAppTab" element={<FeedbackAppTab />} />
            <Route path="/index.html" element={<FeedbackAppTab />} />
            <Route path="/config" element={<FeedbackAppTabConfig />} />
            <Route path="/config.html" element={<FeedbackAppTabConfig />} />
            <Route path="/remove" element={<FeedbackAppTabRemove />} />
            <Route path="/remove.html" element={<FeedbackAppTabRemove />} />
            <Route path="*" element={<Navigate to={"/"} />} />
          </Routes>
        </Router>
      </FluentProvider>
    </TeamsFxContext.Provider>
  );
}
